import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Alert, Snackbar, Chip, Typography, CircularProgress, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import useTableState from '../../hooks/useTableState';
import { facetFilterOptions } from '../common/facetFilter';
import { getMyReferrers, archiveMyReferrer, getReferrerLoginLink, getReferrerShareLink } from '../../api/referralLinkApi';
import ReferrerLoginLinkDialog from './referrerLoginLinkDialog';
import ReferrerShareLinkDialog from './referrerShareLinkDialog';
import { handlePriceWithRM } from '../../utils/priceUtils';
import { convertDateTimeToDateString } from '../../utils/datetimeUtils';
import handleAgentName from '../../utils/userUtils';
import QuickAddReferralDialog from '../sales/quickAddReferralDialog';
import AddReferrersFromCustomersDialog from './addReferrersFromCustomersDialog';
import { useUser } from '../../context/userContext';

/**
 * Referral — the people THIS person introduced.
 *
 * Not the same question as My Referral Report, which is built from sales and answers
 * "which referrers appeared on mine". A referrer signed up five minutes ago has no sale,
 * so that report cannot show them. The link was always recorded (UplineId, stamped by
 * both the quick-add and the invite flow) and simply never read.
 *
 * Built on the same MaterialReactTable setup as the user list — same facet filters, same
 * header and cell styling, same pagination — because it is the same kind of screen and
 * anything else would read as a different app.
 * (user 2026-10-21: "my referral just make it table like user only")
 *
 * Editable here, because the person who created a referrer is the one who can get their
 * bank details — until now only an admin could enter them, or the referrer themselves,
 * who almost never signs in.
 */

const FONT = "'Poppins', sans-serif";

export default function MyReferrersPage() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    // Bulk onboarding of existing customers — Super Admin / Accountant only, same two
    // roles the server checks. (user 2026-10-31)
    const { user } = useUser();
    const roles = Array.isArray(user?.roles) ? user.roles : [];
    const canBulkAdd = roles.includes('Super Admin') || roles.includes('Accountant');
    const [bulkOpen, setBulkOpen] = useState(false);
    // "Send login" — one dialog, filled by the server when it hands back the link.
    const [loginOpen, setLoginOpen] = useState(false);
    const [loginBusy, setLoginBusy] = useState(false);
    const [loginErr, setLoginErr] = useState('');
    const [loginData, setLoginData] = useState(null);
    // The referrer's own /r/ link, for the agent to forward.
    const [shareOpen, setShareOpen] = useState(false);
    const [shareBusy, setShareBusy] = useState(false);
    const [shareErr, setShareErr] = useState('');
    const [shareData, setShareData] = useState(null);
    const ts = useTableState('myReferrers');
    const navigate = useNavigate();

    /* Archive flips Active <-> Archived, the same pair the user list uses. It does NOT
       touch a sale or a fee: Sales.ReferralUserId still points at them and anything
       earned is still owed. What stops is their appearing in the Referral picker on NEW
       sales, which lists Active referrers only - somebody who has stopped referring
       should not keep turning up mid-sale.
       (user 2026-10-21: "edit, archieve by sales person") */
    const toggleArchive = async (r) => {
        const archiving = r.status === 'Active';
        const name = `${r.lastName || ''} ${r.firstName || ''}`.trim() || r.username;
        if (!window.confirm(`${archiving ? 'Archive' : 'Restore'} ${name}?`)) return;
        try {
            await archiveMyReferrer({ userId: r.userId, archive: archiving });
            load();
        } catch {
            setError('Could not change that.');
        }
    };

    /* Minting happens on OPEN, not on a second click inside the dialog: the link is the
       credential, so there is no reason to hold a live one behind a button nobody pressed.
       Each open retires the previous link for that person. */
    const sendLogin = async (r) => {
        setLoginData(null);
        setLoginErr('');
        setLoginBusy(true);
        setLoginOpen(true);
        try {
            const res = await getReferrerLoginLink(r.userId);
            setLoginData(res?.data || null);
        } catch (e) {
            setLoginErr(e?.response?.data?.message || 'Could not make a login link for them.');
        } finally {
            setLoginBusy(false);
        }
    };

    /* Minting happens here rather than on the list, because minting a code for every
       referrer just to draw a column would write a thousand rows nobody asked for. The
       column shows the code once one exists. */
    const shareLink = async (r) => {
        setShareData(null);
        setShareErr('');
        setShareBusy(true);
        setShareOpen(true);
        try {
            const res = await getReferrerShareLink(r.userId);
            setShareData(res?.data || null);
            /* Patch the ONE row rather than reloading the table. load() sets the page-wide
               loading flag, and this component returns a spinner while that is true — so
               the whole page, dialog included, was being torn down and rebuilt the moment
               the link arrived. It looked like the screen refreshed and the dialog only
               then appeared. (user 2026-11-02: "when i click, i will refresh screen then
               only appear this") */
            if (res?.data?.code) {
                setRows(prev => prev.map(x => (
                    x.userId === r.userId ? { ...x, referralCode: res.data.code } : x
                )));
            }
        } catch (e) {
            setShareErr(e?.response?.data?.message || 'Could not get their referral link.');
        } finally {
            setShareBusy(false);
        }
    };

    const load = useCallback(() => {
        setLoading(true);
        getMyReferrers()
            .then(res => setRows(Array.isArray(res?.data) ? res.data : []))
            .catch(() => setError('Could not load your referrers. Please try again.'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    /* The columns the people who use this screen asked for, in their order:
       ref id, name, phone, total case, total sales value, created, is it customer,
       area & state, agent, co-broke agent, status, action.
       (user 2026-11-01)

       IC, Bank and Account No came OUT to make room. They are still on the Edit page,
       and "Bank: Missing" still shows there — but it no longer shouts from the list,
       which is a real loss for chasing payout details and worth putting back if it bites. */
    const columns = useMemo(() => [
        {
            // The ref id IS the user id — sales.ReferralUserId points at this row rather
            // than copying it. Shown plainly rather than dressed up as REF-000296, because
            // a prettified code would be a second identity to reconcile with the one the
            // sale actually stores. (user 2026-10-21: "is ref user id?")
            accessorKey: 'userId', header: 'Ref ID', size: 70,
        },
        {
            id: 'name', header: 'Name', size: 180,
            accessorFn: (r) => handleAgentName(r.firstName, r.lastName) || r.username || '',
        },
        { accessorKey: 'phoneNumber', header: 'Phone', size: 125, Cell: ({ cell }) => cell.getValue() || '—' },
        /* No Referral Link COLUMN. "Share link" in Actions does the same job in the place
           the reader already looks for things to do with a row, and a column of identical
           blue links pushed the figures off the screen.
           (user 2026-11-01: "remove this column cause already got in the end") */
        { accessorKey: 'salesCount', header: 'Total Case', size: 85 },
        {
            accessorKey: 'salesValue', header: 'Total Sales Value', size: 120,
            Cell: ({ cell }) => handlePriceWithRM(Number(cell.getValue()) || 0),
        },
        {
            accessorKey: 'createdOn', header: 'Created', size: 100,
            Cell: ({ cell }) => (cell.getValue() ? convertDateTimeToDateString(cell.getValue()) : '—'),
        },
        {
            // Matched on their phone or IC against the customer on a live sale, the same
            // way Referral Leads answers it — so somebody who bought from us is tellable
            // from somebody who only ever signed up.
            id: 'isCustomer', header: 'Customer?', size: 110,
            accessorFn: (r) => (r.isCustomer ? 'Yes' : 'No'),
            Cell: ({ row }) => (row.original.isCustomer
                ? <Chip size="small" label={row.original.customerSaleId ? `Yes · #${row.original.customerSaleId}` : 'Yes'}
                    sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '0.64rem', height: 20, backgroundColor: '#DBEAFE', color: '#1E40AF' }} />
                : <Chip size="small" label="No"
                    sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '0.64rem', height: 20, backgroundColor: '#F1F5F9', color: '#64748B' }} />),
        },
        {
            // Off their own customer record, which is the only place we hold a structured
            // address — the user table has free text. Blank for a referrer who has never
            // bought from us.
            id: 'area', header: 'Area & State', size: 150,
            accessorFn: (r) => [r.area, r.state].filter(Boolean).join(', '),
            Cell: ({ cell }) => cell.getValue() || '—',
        },
        {
            // Who dealt with their latest introduction. "+2 more" when earlier sales went
            // to different agents — the detail is in those sales, not in this cell.
            id: 'agent', header: 'Agent', size: 160,
            accessorFn: (r) => r.agentName || '',
            Cell: ({ row, cell }) => {
                const v = cell.getValue();
                if (!v) return '—';
                return (
                    <Box>
                        <div>{v}</div>
                        {row.original.otherAgents > 0 && (
                            <div style={{ fontSize: '0.7rem', color: '#94A3B8' }}>
                                +{row.original.otherAgents} more
                            </div>
                        )}
                    </Box>
                );
            },
        },
        {
            id: 'coAgent', header: 'Co-broke Agent', size: 160,
            accessorFn: (r) => r.coAgentName || '',
            Cell: ({ cell }) => cell.getValue() || '—',
        },
        {
            accessorKey: 'status', header: 'Status', size: 90,
            Cell: ({ cell }) => {
                const active = cell.getValue() === 'Active';
                return (
                    <Chip size="small" label={active ? 'Active' : 'Archived'}
                        sx={{
                            fontFamily: FONT, fontWeight: 700, fontSize: '0.64rem', height: 20,
                            backgroundColor: active ? '#D1FAE5' : '#F1F5F9',
                            color: active ? '#065F46' : '#64748B',
                        }} />
                );
            },
        },
        {
            // Text links, not buttons - the same ACTIONS treatment the user list uses, so
            // the two screens read as one app. (user 2026-10-21: "team and user and
            // referral design must be same" / "also need to have edit button etc")
            id: 'actions', header: 'Actions', size: 180,
            enableColumnFilter: false, enableSorting: false,
            Cell: ({ row }) => (
                /* A referrer who is here only because we shared a sale — a co-broke whose
                   referral was the other agent's existing customer — is somebody else's
                   to change. Showing them is right; offering Edit on them is not, and the
                   server would refuse it anyway. (user 2026-10-31) */
                row.original.mine === false ? (
                    <span style={{ color: '#94A3B8', fontSize: '0.8rem' }}>Shared sale</span>
                ) : (
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <span
                        onClick={(e) => { e.stopPropagation(); navigate(`/team/referrals/${row.original.userId}`); }}
                        style={{ color: '#1A8CFF', cursor: 'pointer', fontWeight: 500, fontSize: '0.85rem' }}
                    >
                        Edit
                    </span>
                    {/* Only for an Active referrer — an archived account should not be
                        handed a way back in. */}
                    {row.original.status === 'Active' && (
                        <span
                            onClick={(e) => { e.stopPropagation(); shareLink(row.original); }}
                            style={{ color: '#1A8CFF', cursor: 'pointer', fontWeight: 500, fontSize: '0.85rem' }}
                        >
                            Share link
                        </span>
                    )}
                    {row.original.status === 'Active' && (
                        <span
                            onClick={(e) => { e.stopPropagation(); sendLogin(row.original); }}
                            style={{ color: '#1A8CFF', cursor: 'pointer', fontWeight: 500, fontSize: '0.85rem' }}
                        >
                            Send login
                        </span>
                    )}
                    <span
                        onClick={(e) => { e.stopPropagation(); toggleArchive(row.original); }}
                        style={{ color: row.original.status === 'Active' ? '#64748B' : '#10B981', cursor: 'pointer', fontWeight: 500, fontSize: '0.85rem' }}
                    >
                        {row.original.status === 'Active' ? 'Archive' : 'Restore'}
                    </span>
                </Box>
                )
            ),
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [navigate]);

    const table = useMaterialReactTable({
        ...facetFilterOptions,
        columns,
        data: rows,
        enableRowSelection: false,
        enableColumnActions: false,
        enableColumnDragging: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableHiding: false,
        enableStickyHeader: true,
        initialState: { density: 'compact', showGlobalFilter: true },
        state: { globalFilter: ts.globalFilter, sorting: ts.sorting, pagination: ts.pagination, columnFilters: ts.columnFilters },
        onGlobalFilterChange: ts.setGlobalFilter,
        onSortingChange: ts.setSorting,
        onPaginationChange: ts.setPagination,
        onColumnFiltersChange: ts.setColumnFilters,
        muiTablePaperProps: { elevation: 0, sx: { border: 'none', backgroundColor: 'transparent', borderRadius: '14px' } },
        muiTableContainerProps: { sx: { overflowX: 'auto', maxHeight: 'unset' } },
        muiTableHeadCellProps: { sx: { backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.5px', color: '#64748B', fontFamily: FONT, padding: '8px 10px' } },
        muiTableBodyCellProps: { sx: { fontSize: '0.8rem', color: '#374151', borderBottom: '1px solid #F1F5F9', fontFamily: FONT, padding: '6px 10px' } },
        // Clicking the row opens the editor — the same "the row is the thing" behaviour
        // the user list has, where a row opens that user.
        muiTableBodyRowProps: ({ row }) => (row.original.mine === false ? {
            // Visible because we shared the sale, not ours to open.
            sx: { cursor: 'default' },
        } : {
            onClick: () => navigate(`/team/referrals/${row.original.userId}`),
            sx: { cursor: 'pointer', '&:hover': { backgroundColor: '#F0F7FF', transition: 'background-color 0.12s' } },
        }),
        muiTopToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderBottom: '1px solid #E2E8F0', minHeight: '48px' } },
        muiBottomToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderTop: '1px solid #E2E8F0' } },
        muiSearchTextFieldProps: { placeholder: 'Search referrers...', sx: { '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#F8FAFC', fontSize: '0.8rem', fontFamily: FONT } } },
        muiPaginationProps: { shape: 'rounded', showRowsPerPage: true, rowsPerPageOptions: [10, 25, 50, 100] },
        renderEmptyRowsFallback: () => (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 600, color: '#0F172A', mb: 0.5 }}>
                    No referrers yet
                </Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: '0.85rem', color: '#64748B' }}>
                    Use “New referrer” above, or open the Referral box while creating a sale.
                </Typography>
            </Box>
        ),
    });

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Box>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '1.4rem', color: '#0F172A', mb: 0.5 }}>
                        Referral
                    </Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: '0.85rem', color: '#64748B' }}>
                        People you introduced. You can add one here, or from the Referral box
                        while creating a sale.
                    </Typography>
                </Box>
                {/* Same dialog the sale opens, so the same single endpoint — which can
                    only ever create a Referral, never any other kind of user.
                    (user 2026-10-22: "but only can create referral but not other user") */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {canBulkAdd && (
                        <Button variant="outlined"
                            onClick={() => setBulkOpen(true)}
                            sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}>
                            Add from customers
                        </Button>
                    )}
                    <Button variant="contained" startIcon={<AddIcon />}
                        onClick={() => setAddOpen(true)}
                        sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        New referrer
                    </Button>
                </Box>
            </Box>

            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

            <QuickAddReferralDialog
                open={addOpen}
                onClose={() => setAddOpen(false)}
                onCreated={() => { setAddOpen(false); load(); }}
                submitLabel="Create referrer"
            />

            <AddReferrersFromCustomersDialog
                open={bulkOpen}
                onClose={() => { setBulkOpen(false); load(); }}
                onDone={load}
            />

            <ReferrerShareLinkDialog
                open={shareOpen}
                onClose={() => { setShareOpen(false); setShareData(null); }}
                loading={shareBusy}
                error={shareErr}
                data={shareData}
            />

            <ReferrerLoginLinkDialog
                open={loginOpen}
                onClose={() => { setLoginOpen(false); setLoginData(null); }}
                loading={loginBusy}
                error={loginErr}
                data={loginData}
            />

            <MaterialReactTable table={table} />

            <Snackbar open={saved} autoHideDuration={2500} onClose={() => setSaved(false)} message="Saved" />
        </Box>
    );
}
